export const OFFICE_PREVIEW_MAX_BYTES = 8 * 1024 * 1024;
export const SHEET_PREVIEW_LIMITS = { sheets: 20, rows: 200, columns: 50, characters: 2000 };

// Only this trusted shell is HTML. Document content is created with DOM APIs.
export const WORD_PREVIEW_SHELL = `<!doctype html><html lang="es"><head>
<meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src data:; style-src 'unsafe-inline'; base-uri 'none'; form-action 'none'">
<title>Vista previa Word</title><style>body{margin:0;background:#e2e8f0;color:#111827}img{max-width:100%}</style>
</head><body></body></html>`;

const readBuffer = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(new Error("No se pudo leer el archivo."));
  reader.readAsArrayBuffer(blob);
});

const validateSize = (blob) => {
  if (blob.size > OFFICE_PREVIEW_MAX_BYTES) {
    throw new Error("La vista previa admite archivos de hasta 8 MB. Puedes descargar el original.");
  }
};

// Nodes belong to the CSP-protected frame from creation, not the parent page.
// No event handlers, navigation, embedded HTML, SVG or executable elements.
export const createWordElementFactory = (ownerDocument) => {
  const allowedTags = new Set(["div", "span", "section", "article", "header", "footer", "p", "br", "hr", "table", "thead", "tbody", "tfoot", "tr", "td", "th", "colgroup", "col", "ul", "ol", "li", "sup", "sub", "b", "i", "u", "s", "strong", "em", "img", "style"]);
  const create = (element) => {
    if (typeof element === "string") return ownerDocument.createTextNode(element);
    if (element?.nodeType) return element;
    const { tagName, className, style, children = [], ...props } = element;
    if (tagName === "#comment") return ownerDocument.createComment("");
    const node = tagName === "#fragment"
      ? ownerDocument.createDocumentFragment()
      : ownerDocument.createElement(allowedTags.has(tagName) ? tagName : "span");
    if (className) node.setAttribute("class", className);
    if (style && node.style) {
      if (typeof style === "string") node.setAttribute("style", style);
      else Object.assign(node.style, style);
    }
    for (const name of ["id", "title", "alt", "colSpan", "rowSpan"]) {
      if (props[name] !== undefined && node.nodeType === 1) node.setAttribute(name, String(props[name]));
    }
    children.filter((child) => child !== null && child !== undefined).forEach((child) => node.appendChild(create(child)));
    return node;
  };
  return create;
};

export const renderWordPreview = async (blob, container, isCurrent = () => true) => {
  validateSize(blob);
  const { renderAsync } = await import("docx-preview");
  const buffer = await readBuffer(blob);
  if (!isCurrent()) return;
  try {
    await renderAsync(buffer, container, container, {
      h: createWordElementFactory(container.ownerDocument),
      renderAltChunks: false,
      renderComments: false,
      ignoreFonts: true,
      useBase64URL: true,
      experimental: false,
    });
  } finally {
    if (!isCurrent()) container.replaceChildren();
  }
};

const cellText = (value) => {
  if (value === null || value === undefined) return "";
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? "" : value.toISOString();
  return String(value);
};

export const boundWorkbook = (sheets) => {
  const limits = SHEET_PREVIEW_LIMITS;
  return {
    sheetsTruncated: sheets.length > limits.sheets,
    sheets: sheets.slice(0, limits.sheets).map(({ sheet, data }) => {
      const visible = data.slice(0, limits.rows);
      let truncated = data.length > limits.rows;
      const rows = visible.map((row) => {
        if (row.length > limits.columns) truncated = true;
        return row.slice(0, limits.columns).map((value) => {
          const text = cellText(value);
          if (text.length > limits.characters) truncated = true;
          return text.length > limits.characters ? `${text.slice(0, limits.characters)}…` : text;
        });
      });
      return { name: sheet, rows, truncated };
    }),
  };
};

export const readWorkbookPreview = async (blob, isCurrent = () => true) => {
  validateSize(blob);
  const { default: readExcelFile } = await import("read-excel-file/browser");
  const buffer = await readBuffer(blob);
  if (!isCurrent()) return null;
  // The parser reads stored values, not executable formulas or HTML.
  const sheets = await readExcelFile(buffer);
  return isCurrent() ? boundWorkbook(sheets) : null;
};
